import { ReactNode } from "react";
import { Alert, AlertTitle, Box, Link, Typography } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";

import type { PdfActiveJobRef } from "features/pdfTools/pdfTools";
import { formatSplitDateTime } from "./splitJobUi";
import { splitJobPath } from "./splitRoutes";

export interface SplitBusySlotsAlertProps {
  message: string;
  activeJobs: PdfActiveJobRef[];
  /** What the operator does next, under the title. */
  note: ReactNode;
}

/**
 * The global split limit is reached: which jobs hold the slots, each a link to
 * its page, so the operator can see what to wait for or what to stop.
 */
export default function SplitBusySlotsAlert({
  message,
  activeJobs,
  note,
}: SplitBusySlotsAlertProps) {
  return (
    <Alert severity="warning" sx={{ "& .MuiAlert-message": { minWidth: 0 } }}>
      <AlertTitle sx={{ fontWeight: 700 }}>{message}</AlertTitle>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {note}
      </Typography>
      {activeJobs.map((active) => {
        // Two jobs of one client read alike: the start time tells them apart.
        const started = active.started_at
          ? `, started ${formatSplitDateTime(active.started_at, { short: true })}`
          : ", waiting to start";
        const label = `${active.user.username} - ${active.splitter_label}${started}`;
        return (
          <Box key={active.job_id} sx={{ display: "flex", gap: 0.5, alignItems: "center", minWidth: 0 }}>
            {active.user.id != null && active.major_job ? (
              <Link
                href={splitJobPath(active.user.id, active.major_job.id, active.job_id)}
                target="_blank"
                rel="noopener"
                variant="body2"
                title={label}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                  overflowWrap: "anywhere",
                }}
              >
                {label}
                <OpenInNew sx={{ fontSize: 14, flexShrink: 0 }} />
              </Link>
            ) : (
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                {label}
              </Typography>
            )}
          </Box>
        );
      })}
    </Alert>
  );
}
